import { useEffect, useState } from 'react';
import type { CardIdentity, CardPresentation, CardRecord } from '../shared/types';
import { shortDateTimeWithYear } from '../shared/datetime';
import { useCatalogApi } from './catalog-api';
import {
  cardResearchQuery,
  centsToUsdInput,
  ebaySearchUrl,
  POINT130_SEARCH_URL,
  usdInputToCents,
} from './card-research';

export interface CardResearchPricingDraft {
  query: string;
  price: string;
  notes: string;
}

export function cardResearchPricingDraft(
  card: CardRecord,
  identity: Partial<CardIdentity>,
): CardResearchPricingDraft {
  return {
    query: card.listingPrice?.researchQuery ?? cardResearchQuery(identity, card.cardPresentation),
    price: centsToUsdInput(card.listingPrice?.amountMinor),
    notes: card.listingPrice?.notes ?? '',
  };
}

export function adjustUsdInputByDollars(value: string, delta: -1 | 1): string {
  const amountMinor = usdInputToCents(value);
  if (amountMinor === null) return delta > 0 ? '1.00' : '0.01';
  return centsToUsdInput(Math.max(1, amountMinor + delta * 100));
}

/** Shared controlled input; the enclosing review owns validation and persistence. */
export function ListingPriceInput({
  cardId,
  price,
  disabled = false,
  onChange,
}: {
  cardId: string;
  price: string;
  disabled?: boolean;
  onChange: (price: string) => void;
}): JSX.Element {
  return (
    <div className="tcvs-field">
      <label htmlFor={`listing-price-${cardId}`}>Listing price (USD)</label>
      <div className="tcvs-currency-stepper">
        <div className="tcvs-currency-input">
          <span>$</span>
          <input
            id={`listing-price-${cardId}`}
            aria-label="Listing price in USD"
            inputMode="decimal"
            placeholder="0.00"
            value={price}
            disabled={disabled}
            onChange={(event) => onChange(event.target.value)}
          />
        </div>
        <div className="tcvs-currency-stepper-controls">
          <button
            type="button"
            className="tcvs-icon-button"
            aria-label="Increase listing price by one dollar"
            title="Increase by $1"
            disabled={disabled}
            onClick={() => onChange(adjustUsdInputByDollars(price, 1))}
          >
            <span aria-hidden="true">&uarr;</span>
          </button>
          <button
            type="button"
            className="tcvs-icon-button"
            aria-label="Decrease listing price by one dollar"
            title="Decrease by $1"
            disabled={disabled}
            onClick={() => onChange(adjustUsdInputByDollars(price, -1))}
          >
            <span aria-hidden="true">&darr;</span>
          </button>
        </div>
      </div>
    </div>
  );
}

export function CardResearchPricing({
  card,
  identity,
  onChanged,
  embedded = false,
  draft,
  onDraftChange,
  submitWithReview = false,
  disabled = false,
  cardPresentation,
  showPriceEditor = true,
}: {
  card: CardRecord;
  identity: Partial<CardIdentity>;
  onChanged: () => void | Promise<void>;
  embedded?: boolean;
  draft?: CardResearchPricingDraft;
  onDraftChange?: (draft: CardResearchPricingDraft) => void;
  submitWithReview?: boolean;
  disabled?: boolean;
  cardPresentation?: CardPresentation;
  showPriceEditor?: boolean;
}): JSX.Element {
  const catalog = useCatalogApi();
  const suggestedQuery = cardResearchQuery(identity, cardPresentation ?? card.cardPresentation);
  const [localDraft, setLocalDraft] = useState<CardResearchPricingDraft>(() =>
    cardResearchPricingDraft(card, identity),
  );
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [copyMessage, setCopyMessage] = useState<string | null>(null);
  const currentDraft = draft ?? localDraft;
  const { query, price, notes } = currentDraft;

  const updateDraft = (next: CardResearchPricingDraft): void => {
    if (onDraftChange !== undefined) onDraftChange(next);
    else setLocalDraft(next);
  };

  useEffect(() => {
    if (draft === undefined) {
      setLocalDraft({
        query: card.listingPrice?.researchQuery ?? suggestedQuery,
        price: centsToUsdInput(card.listingPrice?.amountMinor),
        notes: card.listingPrice?.notes ?? '',
      });
    }
    setError(null);
    setCopyMessage(null);
  }, [card.cardId, card.listingPrice, draft, suggestedQuery]);

  const copyQueryFor130Point = (): void => {
    void navigator.clipboard
      .writeText(query.trim())
      .then(() => setCopyMessage('Query copied. Paste it into 130 Point.'))
      .catch(() => setCopyMessage('Copy failed. Copy the comparable search text manually.'));
  };

  const amountMinor = usdInputToCents(price);
  const save = async (): Promise<void> => {
    if (amountMinor === null || busy) return;
    setBusy(true);
    setError(null);
    const result = await catalog.updateListingPrice(card.cardId, {
      amountMinor,
      currency: 'USD',
      ...(query.trim() === '' ? {} : { researchQuery: query.trim() }),
      ...(notes.trim() === '' ? {} : { notes: notes.trim() }),
    });
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    await onChanged();
  };

  const clear = async (): Promise<void> => {
    if (
      busy ||
      card.listingPrice === undefined ||
      !window.confirm("Clear this card's reviewed listing price?")
    )
      return;
    setBusy(true);
    setError(null);
    const result = await catalog.updateListingPrice(card.cardId, null);
    setBusy(false);
    if (!result.ok) {
      setError(result.message ?? result.error);
      return;
    }
    await onChanged();
  };

  return (
    <section className={embedded ? 'tcvs-research-pricing' : 'tcvs-card tcvs-research-pricing'}>
      <div className="tcvs-research-pricing-heading">
        <div>
          <h3>{showPriceEditor ? 'Research and listing price' : 'Pricing research'}</h3>
          {card.listingPrice !== undefined ? (
            <p className="tcvs-muted">
              Reviewed {shortDateTimeWithYear(card.listingPrice.reviewedAt)}
            </p>
          ) : null}
        </div>
        <div className="tcvs-research-links">
          <a href={ebaySearchUrl(query)} target="_blank" rel="noreferrer">
            eBay active
          </a>
          <a href={ebaySearchUrl(query, true)} target="_blank" rel="noreferrer">
            eBay sold
          </a>
          <a
            href={POINT130_SEARCH_URL}
            target="_blank"
            rel="noreferrer"
            onClick={copyQueryFor130Point}
          >
            130 Point sold
          </a>
        </div>
      </div>
      <label className="tcvs-field">
        <span>Comparable search</span>
        <input
          value={query}
          maxLength={300}
          disabled={disabled}
          onChange={(event) => {
            updateDraft({ ...currentDraft, query: event.target.value });
            setCopyMessage(null);
          }}
        />
      </label>
      {copyMessage !== null ? (
        <p className="tcvs-muted" role="status">
          {copyMessage}
        </p>
      ) : null}
      <div className="tcvs-research-price-row">
        {showPriceEditor ? (
          <ListingPriceInput
            cardId={card.cardId}
            price={price}
            disabled={disabled}
            onChange={(price) => updateDraft({ ...currentDraft, price })}
          />
        ) : null}
        <label className="tcvs-field tcvs-pricing-notes">
          <span>Pricing notes</span>
          <textarea
            rows={2}
            value={notes}
            maxLength={500}
            placeholder="Optional rationale or comparable listings"
            disabled={disabled}
            onChange={(event) => updateDraft({ ...currentDraft, notes: event.target.value })}
          />
        </label>
      </div>
      {showPriceEditor && price !== '' && amountMinor === null ? (
        <p className="tcvs-error">Enter a price from $0.01 to $1,000,000.00.</p>
      ) : null}
      {submitWithReview ? (
        showPriceEditor ? (
          <p className="tcvs-muted">The listing price is saved with the verified identity.</p>
        ) : null
      ) : (
        <div className="tcvs-research-pricing-actions">
          <button type="button" disabled={amountMinor === null || busy} onClick={() => void save()}>
            {busy ? 'Saving...' : 'Save listing price'}
          </button>
          {card.listingPrice !== undefined ? (
            <button
              type="button"
              className="tcvs-btn-secondary"
              disabled={busy}
              onClick={() => void clear()}
            >
              Clear price
            </button>
          ) : null}
        </div>
      )}
      {error !== null ? <p className="tcvs-error">Price update failed: {error}</p> : null}
    </section>
  );
}
